import { injectable } from 'tsyringe';
import platform from 'platform';
import log from 'loglevel';

export type DeviceType = 'windows' | 'mac' | 'mobile' | 'tablet' | 'other';

export const TOKEN_IDeviceCapabilities = Symbol('IDeviceCapabilities');

export interface IDeviceCapabilities {

    /**
     * Check if the user is on windows
     */
    get isWindows(): boolean;

    /**
     * Returns the type of device the user is currently using
     */
    get getDeviceType(): DeviceType;

    /**
     * Returns true if the device is touch-only (no mouse/trackpad available)
     */
    get isTouchOnly(): boolean;

    /**
     * Returns true if the browser supports WebGPU
     */
    get hasWebGPUSupport(): boolean;

    /**
     * Returns true if the device has touch capability
     */
    get hasTouchCapability(): boolean;
}

@injectable()
export class DeviceCapabilities implements IDeviceCapabilities {
    private deviceType: DeviceType;
    private touchOnlyDevice: boolean;
    private webGPUSupported: boolean;
    private touchCapability: boolean;

    constructor() {
        log.info('Initializing DeviceCapabilities service');
        this.deviceType = this.detectDeviceType();
        this.touchCapability = this.detectTouchCapability();
        this.touchOnlyDevice = this.detectTouchOnly();
        this.webGPUSupported = this.detectWebGPUSupport();
        log.info(`Device capabilities detected:
            - Device type: ${this.deviceType}
            - Touch capability: ${this.touchCapability}
            - Touch only: ${this.touchOnlyDevice}
            - WebGPU support: ${this.webGPUSupported}
            - OS: ${platform.os?.family || 'Unknown'}
            - Browser: ${platform.name} ${platform.version}`);
    }

    public get getDeviceType(): DeviceType {
        return this.deviceType;
    }

    public get isWindows(): boolean {
        return this.getDeviceType === 'windows';
    }

    public get isTouchOnly(): boolean {
        return this.touchOnlyDevice;
    }

    public get hasWebGPUSupport(): boolean {
        return this.webGPUSupported;
    }

    public get hasTouchCapability(): boolean {
        return this.touchCapability;
    }

    private detectDeviceType = (): DeviceType => {
        const os = platform.os?.family || '';
        const ua = navigator.userAgent;

        log.info(`Detecting device type - OS: ${os}, User Agent: ${ua}`);

        // Check for tablets first (some tablets report as mobile)
        if (/iPad|Android(?!.*mobile)|Tablet/i.test(ua)) {
            log.info('Device detected as tablet');
            return 'tablet';
        }

        // Check for mobile devices
        if (/iPhone|iPod|Android.*Mobile|BlackBerry|webOS/i.test(ua)) {
            log.info('Device detected as mobile');
            return 'mobile';
        }

        // Check desktop OS
        if (os.includes('Windows')) {
            log.info('Device detected as Windows');
            return 'windows';
        }

        if (os.includes('OS X') || os.includes('Mac')) {
            log.info('Device detected as Mac');
            return 'mac';
        }

        log.info('Device type not specifically recognized, categorizing as other');
        return 'other';
    }

    private detectTouchCapability = (): boolean => {
        // Check if device has touch capability
        const hasTouchCapability =
            typeof window !== 'undefined' &&
            ('ontouchstart' in window ||
                navigator.maxTouchPoints > 0 ||
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                (navigator.msMaxTouchPoints > 0));

        log.info(`Touch capability detection: ${hasTouchCapability ? 'Touch capable' : 'No touch capability'}`);

        return hasTouchCapability;
    }

    private detectTouchOnly = (): boolean => {
        // First check if device has touch capability
        const hasTouchCapability = this.touchCapability;

        // Then check if device has fine pointer capability
        // Devices with a fine pointer are not touch-only devices
        const hasCoarsePointer = typeof window !== 'undefined' &&
            window.matchMedia('(pointer: coarse)').matches;

        const hasFinePointer = typeof window !== 'undefined' &&
            window.matchMedia('(pointer: fine)').matches;

        // A device is touch-only if it has touch capability AND has a coarse pointer
        // but NOT a fine pointer (mouse/trackpad)
        const isTouchOnly = hasTouchCapability && hasCoarsePointer && !hasFinePointer;

        log.info(`Touch-only detection:
            - Has touch capability: ${hasTouchCapability}
            - Has coarse pointer: ${hasCoarsePointer}
            - Has fine pointer: ${hasFinePointer}
            - Is touch-only: ${isTouchOnly}`);

        return isTouchOnly;
    }

    private detectWebGPUSupport = (): boolean => {
        // Check if WebGPU is available in the navigator
        const hasWebGPU = !!(navigator.gpu);
        log.info(`WebGPU support detection: ${hasWebGPU ? 'Supported' : 'Not supported'}`);
        return hasWebGPU;
    }
}